/**
 * IndexedDB wrapper for offline-first adventure caching
 * Provides structured storage for adventures and search results
 */

const DB_NAME = 'HiddenAdventuresDB'
const DB_VERSION = 1

// Object store names
export const STORES = {
  ADVENTURES: 'adventures',
  SEARCH_RESULTS: 'searchResults',
} as const

interface DBSchema {
  adventures: {
    key: string // adventure ID
    value: {
      id: string
      data: any // AdventureDto
      lastRefreshed: number
      createdAt: number
    }
    indexes: {
      lastRefreshed: number
    }
  }
  searchResults: {
    key: string // cache key generated from search params
    value: {
      cacheKey: string
      data: any // PaginatedResponse<AdventureDto>
      searchParams: any // AdventureSearchRequest
      lastRefreshed: number
      createdAt: number
    }
    indexes: {
      lastRefreshed: number
    }
  }
}

/**
 * Open or create the database
 */
export function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => {
      reject(new Error(`Failed to open database: ${request.error}`))
    }

    request.onsuccess = () => {
      resolve(request.result)
    }

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result

      // Create Adventures store
      if (!db.objectStoreNames.contains(STORES.ADVENTURES)) {
        const adventuresStore = db.createObjectStore(STORES.ADVENTURES, {
          keyPath: 'id',
        })
        adventuresStore.createIndex('lastRefreshed', 'lastRefreshed', {
          unique: false,
        })
      }

      // Create Search Results store
      if (!db.objectStoreNames.contains(STORES.SEARCH_RESULTS)) {
        const searchStore = db.createObjectStore(STORES.SEARCH_RESULTS, {
          keyPath: 'cacheKey',
        })
        searchStore.createIndex('lastRefreshed', 'lastRefreshed', {
          unique: false,
        })
      }
    }
  })
}

/**
 * Generic get operation
 */
export function get<T>(
  storeName: string,
  key: string
): Promise<T | undefined> {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await openDB()
      const transaction = db.transaction(storeName, 'readonly')
      const store = transaction.objectStore(storeName)
      const request = store.get(key)

      request.onsuccess = () => {
        resolve(request.result as T | undefined)
      }

      request.onerror = () => {
        reject(new Error(`Failed to get from ${storeName}: ${request.error}`))
      }
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Generic put operation
 */
export function put<T>(storeName: string, value: T): Promise<void> {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await openDB()
      const transaction = db.transaction(storeName, 'readwrite')
      const store = transaction.objectStore(storeName)
      const request = store.put(value)

      request.onsuccess = () => {
        resolve()
      }

      request.onerror = () => {
        reject(new Error(`Failed to put to ${storeName}: ${request.error}`))
      }
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Generic delete operation
 */
export function deleteRecord(storeName: string, key: string): Promise<void> {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await openDB()
      const transaction = db.transaction(storeName, 'readwrite')
      const store = transaction.objectStore(storeName)
      const request = store.delete(key)

      request.onsuccess = () => {
        resolve()
      }

      request.onerror = () => {
        reject(
          new Error(`Failed to delete from ${storeName}: ${request.error}`)
        )
      }
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Get all records from a store
 */
export function getAll<T>(storeName: string): Promise<T[]> {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await openDB()
      const transaction = db.transaction(storeName, 'readonly')
      const store = transaction.objectStore(storeName)
      const request = store.getAll()

      request.onsuccess = () => {
        resolve(request.result as T[])
      }

      request.onerror = () => {
        reject(new Error(`Failed to getAll from ${storeName}: ${request.error}`))
      }
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Get records by index
 */
export function getByIndex<T>(
  storeName: string,
  indexName: string,
  query: IDBValidKey | IDBKeyRange
): Promise<T[]> {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await openDB()
      const transaction = db.transaction(storeName, 'readonly')
      const store = transaction.objectStore(storeName)
      const index = store.index(indexName)
      const request = index.getAll(query)

      request.onsuccess = () => {
        resolve(request.result as T[])
      }

      request.onerror = () => {
        reject(
          new Error(
            `Failed to getByIndex from ${storeName}.${indexName}: ${request.error}`
          )
        )
      }
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Delete records older than a certain timestamp
 */
export function deleteOlderThan(
  storeName: string,
  timestamp: number
): Promise<number> {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await openDB()
      const transaction = db.transaction(storeName, 'readwrite')
      const store = transaction.objectStore(storeName)
      const index = store.index('lastRefreshed')

      // Get all records older than timestamp
      const range = IDBKeyRange.upperBound(timestamp)
      const request = index.openCursor(range)

      let deletedCount = 0

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result as IDBCursorWithValue
        if (cursor) {
          cursor.delete()
          deletedCount++
          cursor.continue()
        } else {
          resolve(deletedCount)
        }
      }

      request.onerror = () => {
        reject(
          new Error(
            `Failed to deleteOlderThan from ${storeName}: ${request.error}`
          )
        )
      }
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Clear all data from a store
 */
export function clearStore(storeName: string): Promise<void> {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await openDB()
      const transaction = db.transaction(storeName, 'readwrite')
      const store = transaction.objectStore(storeName)
      const request = store.clear()

      request.onsuccess = () => {
        resolve()
      }

      request.onerror = () => {
        reject(new Error(`Failed to clear ${storeName}: ${request.error}`))
      }
    } catch (error) {
      reject(error)
    }
  })
}

/**
 * Get count of records in a store
 */
export function count(storeName: string): Promise<number> {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await openDB()
      const transaction = db.transaction(storeName, 'readonly')
      const store = transaction.objectStore(storeName)
      const request = store.count()

      request.onsuccess = () => {
        resolve(request.result)
      }

      request.onerror = () => {
        reject(new Error(`Failed to count ${storeName}: ${request.error}`))
      }
    } catch (error) {
      reject(error)
    }
  })
}
